import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { catchError } from 'rxjs/operators';
import {
  Campus,
  Cluster,
  Room,
  Batch,
  Team,
  User,
  Date,
  Timeline,
  TimelineCounts,
  Conference,
  DateTimeFrame,
  CLUSTERS as CLUSTER_NAMES,
 } from '../models';
import { environment } from '../../../environments/environment';
import { SnackbarService } from './snackbar.service';

const BATCHES = '/batches';
const TEAMS = '/teams';
const TEAM = '/team';
const USERS = '/users';
const STUDENTS = '/students';
const CAMPUSES = '/campuses';
const CLUSTERS = '/clusters';
const CLUSTER = '/cluster';
const ROOMS = '/rooms';
const ROOM = '/room';
const CONFERENCES = '/conferences';
const CONFERENCE = '/conference';
const TIMELINES = '/timelines';
const COUNTS = '/counts';

const SCHEDULE = '/schedule';
const PREFERENCES = '/preferences';

const ALL = '';
const MY = '/my';
const ME = '/me';
const AVAILABLE = '/available';

const URL = environment.API_URL + '/api';

@Injectable({ providedIn: 'root' })
export class ApiService {

  constructor(
    private http: HttpClient,
    private snackBarService: SnackbarService,
  ) { }

  public getMyCampus(): Observable<Campus> {
    return this.http.get<Campus>(`${URL}${CAMPUSES}${MY}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    )
  }

  public getMyBatch(): Observable<Batch> {
    return this.http.get<Batch>(`${URL}${BATCHES}${MY}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    )
  }

  public getMyTeam(): Observable<Team> {
    return this.http.get<Team>(`${URL}${TEAMS}${MY}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    )
  }

  public getTeamOf(user: User): Observable<Team> {
    let params = new HttpParams()
      .set('user-id', user.id.toString())
    return this.http.get<Team>(`${URL}${TEAMS}${TEAM}`, { params: params }).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    )
  }

  public getStudent(id: number): Observable<User> {
    return this.http.get<User>(`${URL}${USERS}/${id}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    )
  }

  public getMe(): Observable<User> {
    return this.http.get<User>(`${URL}${USERS}${ME}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    )
  }

  public updatePassword(user: User, batch: Batch, team: Team, campus: Campus, password: string): Observable<any> {
    let params = new HttpParams()
      .set('first-name', user.firstName)
      .set('last-name', user.lastName)
      .set('role', user.role)
      .set('batch-number', batch.number.toString())
      .set('team-name', team.name)
      .set('campus-location', campus.location)
      .set('password', password)

    return this.http.put(`${URL}${STUDENTS}/${user.id}`, null, { params: params, responseType: 'text' }).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getCluster(clusterName: string): Observable<Cluster> {
    return this.http.get<Cluster>(`${URL}${CLUSTERS}/${clusterName}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getMyCluster(): Observable<Cluster> {
    return this.http.get<Cluster>(`${URL}${CLUSTERS}/${MY}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getRoom(roomName: string): Observable<Room> {
    return this.http.get<Room>(`${URL}${ROOMS}/${roomName}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getAvailableRooms(possibleConference: Conference, clusterName: string): Observable<Room[]> {
    let params = new HttpParams()
      .set('year', possibleConference.date.year.toString())
      .set('month', possibleConference.date.month.toString())
      .set('day', possibleConference.date.day.toString())
      .set('conference-type', possibleConference.type.toString())
      .set('cluster-name', clusterName);

    for (const timeline of possibleConference.timelines) {
      params = params.append('timeline-id', timeline.id.toString());
    }
    return this.http.get<Room[]>(`${URL}${ROOMS}${AVAILABLE}`, { params: params }).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getDateTimeFrame(): Observable<DateTimeFrame> {
    return this.http.get<DateTimeFrame>(`${URL}${SCHEDULE}${PREFERENCES}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getMyTimelineCounts(date: Date): Observable<TimelineCounts> {
    let params = new HttpParams()
      .set('year', date.year.toString())
      .set('month', date.month.toString())
      .set('day', date.day.toString())

  return this.http.get<TimelineCounts>(`${URL}${TIMELINES}${COUNTS}${MY}`, { params: params }).pipe(
    catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
  );
  }

  public getMyConferences(): Observable<Conference[]> {
    return this.http.get<Conference[]>(`${URL}${CONFERENCES}${MY}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getRoomConferences(roomName: string): Observable<Conference[]> {
    let params = new HttpParams()
      .set('room-name', roomName)
 
    return this.http.get<Conference[]>(`${URL}${CONFERENCES}${ROOM}`, { params: params }).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public getClusterConferences(clusterName: string): Observable<Conference[]> {
    let params = new HttpParams()
      .set('cluster-name', clusterName);
 
    return this.http.get<Conference[]>(`${URL}${CONFERENCES}${CLUSTER}`, { params: params }).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }

  public bookConference(conference: Conference, notifyTeam: boolean): Observable<Conference> {
    let params = new HttpParams()
      .set('room-id', conference.room.id.toString())
      .set('year', conference.date.year.toString())
      .set('month', conference.date.month.toString())
      .set('day', conference.date.day.toString())
      .set('conference-type', conference.type.toString())
      .set('notify-team', String(notifyTeam));

    for (const timeline of conference.timelines) {
      params = params.append('timeline-id', timeline.id.toString());
    }
    return this.http.post<any>(`${URL}${CONFERENCES}${CONFERENCE}`, null, { params: params });
  }

  public cancelConference(conference: Conference): Observable<any> {
    return this.http.delete<any>(`${URL}${CONFERENCES}/${conference.id}`).pipe(
      catchError((e: any) => Observable.throw(this.snackBarService.showError(e)))
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/api.service.ts